import { ApiError, type ApiErrorDto } from './types';

export const API_BASE = import.meta.env.VITE_API_BASE ?? 'https://admin.coolbalik.cz';

const TOKEN_KEY = 'auth_token';

export const tokenStore = {
  get: () => localStorage.getItem(TOKEN_KEY),
  set: (token: string) => localStorage.setItem(TOKEN_KEY, token),
  clear: () => localStorage.removeItem(TOKEN_KEY),
};

let onUnauthorized: (() => void) | null = null;
export function setUnauthorizedHandler(fn: () => void) {
  onUnauthorized = fn;
}

interface RequestOptions {
  method?: 'GET' | 'POST' | 'PUT';
  body?: unknown;
  formData?: FormData;
  auth?: boolean;
}

export async function request<T>(path: string, opts: RequestOptions = {}): Promise<T> {
  const { method = 'POST', body, formData, auth = true } = opts;

  const headers: Record<string, string> = {};
  if (auth) {
    const token = tokenStore.get();
    if (token) headers['Authorization'] = `Bearer ${token}`;
  }
  // U FormData hlavičku Content-Type nenastavujeme – prohlížeč doplní boundary.
  if (!formData && body !== undefined) headers['Content-Type'] = 'application/json';

  let res: Response;
  try {
    res = await fetch(API_BASE + path, {
      method,
      headers,
      body: formData ?? (body !== undefined ? JSON.stringify(body) : undefined),
      credentials: 'include',
    });
  } catch (err) {
    throw new ApiError('NETWORK', err instanceof Error ? err.message : null);
  }

  if (res.ok) {
    if (res.status === 204) return undefined as T;
    return (await res.json()) as T;
  }

  // Chybové tělo čteme kvůli kódu `error`; text z něj se uživateli NIKDY nezobrazí.
  const payload = await safeJson(res);
  const code = payload?.error ?? null;
  const detail = payload?.message ?? null;

  if (res.status === 401) {
    tokenStore.clear();
    onUnauthorized?.();
    throw new ApiError('UNAUTHORIZED', detail, 401, code);
  }
  // 403 = token platí, jen role na tenhle endpoint nestačí. Odhlašovat se
  // kvůli tomu nesmí – skladník by přišel o směnu kvůli tlačítku navíc.
  if (res.status === 403) throw new ApiError('FORBIDDEN', detail, 403, code);
  if (res.status === 404) throw new ApiError('NOT_FOUND', detail, 404, code);
  if (res.status === 409) throw new ApiError('CONFLICT', detail, 409, code);

  throw new ApiError('SERVER', detail, res.status, code);
}

async function safeJson(res: Response): Promise<ApiErrorDto | null> {
  try {
    return (await res.json()) as ApiErrorDto;
  } catch {
    return null;
  }
}
